import { io, type Socket } from 'socket.io-client';
// From the dependency-free entry point: importing these from the barrel
// would pull Zod and every schema into the widget bundle.
import { HEARTBEAT_INTERVAL_MS, SOCKET_PATH } from '@desk/contracts/constants';

/**
 * The realtime connection.
 *
 * Two things this deliberately does not do. It does not send credentials in the
 * handshake payload — identity comes from the cookie the browser attaches, so a
 * client cannot assert who it is. And it does not carry audio: in the reference
 * system this same channel carried 16 kHz PCM at ~32 KB/s per speaking
 * endpoint, which is the single design decision this architecture exists to
 * undo.
 */

export type AckResult<T> = { ok: true; data: T } | { ok: false; error: string };

export interface SocketOptions {
  url: string;
  namespace: string;
  onConnect?: () => void;
  onDisconnect?: (reason: string) => void;
  onError?: (message: string) => void;
}

export class DeskSocket {
  readonly raw: Socket;
  private heartbeat: ReturnType<typeof setInterval> | null = null;

  constructor(private readonly opts: SocketOptions) {
    this.raw = io(`${opts.url}${opts.namespace}`, {
      path: SOCKET_PATH,
      withCredentials: true,
      transports: ['websocket', 'polling'],
      // socket.io-client caches one Manager per origin and multiplexes
      // namespaces over it. Two DeskSockets for different namespaces would
      // therefore share a connection — and whichever was created first would
      // decide the credentials both handshakes used. Each DeskSocket owns its
      // own connection instead.
      forceNew: true,
      // Reconnect forever with backoff. A support desk that gives up after ten
      // tries is a support desk that silently stops receiving customers.
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 500,
      reconnectionDelayMax: 10_000,
      randomizationFactor: 0.5,
    });

    this.raw.on('connect', () => {
      opts.onConnect?.();
      this.startHeartbeat();
    });

    this.raw.on('disconnect', (reason) => {
      this.stopHeartbeat();
      opts.onDisconnect?.(reason);
    });

    this.raw.on('connect_error', (err) => opts.onError?.(err.message));
  }

  /**
   * Application heartbeat on top of the transport ping.
   *
   * The transport ping proves the socket is open; this proves the *session*
   * behind it is still valid and refreshes the presence TTL. They are not the
   * same question, and only the second one keeps an agent in the routing pool.
   */
  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeat = setInterval(() => this.raw.emit('heartbeat'), HEARTBEAT_INTERVAL_MS);
  }

  private stopHeartbeat(): void {
    if (this.heartbeat) clearInterval(this.heartbeat);
    this.heartbeat = null;
  }

  on<T = unknown>(event: string, handler: (payload: T) => void): () => void {
    this.raw.on(event, handler as (...args: unknown[]) => void);
    return () => this.raw.off(event, handler as (...args: unknown[]) => void);
  }

  /**
   * Emits and waits for the server's acknowledgement.
   *
   * Every state-changing client action goes through here, because the client
   * changes nothing until the server confirms. That is the reference system's
   * best correctness decision and the reason a reload, a second tab or a
   * ten-second network blip cannot leave the two endpoints disagreeing.
   */
  emitWithAck<T>(event: string, payload: unknown = {}, timeoutMs = 10_000): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      let settled = false;
      const timer = setTimeout(() => {
        if (settled) return;
        settled = true;
        reject(new Error('The server did not respond. Check your connection.'));
      }, timeoutMs);

      this.raw.emit(event, payload, (res: AckResult<T>) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        if (res && res.ok) resolve(res.data);
        else reject(new Error(res?.error ?? 'Request failed'));
      });
    });
  }

  get connected(): boolean {
    return this.raw.connected;
  }

  close(): void {
    this.stopHeartbeat();
    this.raw.close();
  }
}
